import { Component } from '@theme/component';

/**
 * @typedef {Object} FlavorPickerRefs
 * @property {HTMLElement[]} pills
 * @property {HTMLElement[]} cards
 */

/**
 * Flavor picker — a WAI-ARIA tabs widget where each tab reveals one product card.
 *
 * The markup ships with the first card visible and the rest `hidden`, so without
 * JS the section still renders a usable card instead of collapsing.
 *
 * @extends {Component<FlavorPickerRefs>}
 */
export class FlavorPickerComponent extends Component {
  requiredRefs = ['pills', 'cards'];

  #active = 0;

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  /**
   * Re-bind after the Section Rendering API swaps the markup: the old pill
   * elements are gone, so the listeners bound to them have to go with them.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  #bind() {
    const { pills } = this.refs;
    if (!pills?.length) return;

    // A reconnect (or a section re-render) runs this again; without aborting the
    // previous controller the old listeners stack up on every pass.
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#active = Math.max(
      0,
      pills.findIndex((pill) => pill.getAttribute('aria-selected') === 'true')
    );

    for (const [index, pill] of pills.entries()) {
      pill.addEventListener('click', () => this.#activate(index), { signal });
    }

    this.addEventListener('keydown', (event) => this.#handleKeydown(event), { signal });

    this.#sync();
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { pills } = this.refs;
    if (!pills?.length) return;

    const index = pills.indexOf(target);
    if (index === -1) return;

    /** @type {Record<string, number>} */
    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -index,
      End: pills.length - 1 - index,
    };

    const offset = navMap[event.key];
    if (offset === undefined) return;

    event.preventDefault();
    const next = (index + offset + pills.length) % pills.length;
    this.#activate(next);
    pills[next]?.focus();
  }

  /** @param {number} index */
  #activate(index) {
    const { pills } = this.refs;
    if (!pills || index === this.#active || index < 0 || index >= pills.length) return;

    this.#active = index;
    this.#sync();
  }

  #sync() {
    const { pills, cards } = this.refs;

    for (const [index, pill] of pills?.entries() ?? []) {
      const isActive = index === this.#active;
      pill.setAttribute('aria-selected', String(isActive));
      pill.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [index, card] of cards?.entries() ?? []) {
      card.hidden = index !== this.#active;
    }
  }
}

if (!customElements.get('flavor-picker')) {
  customElements.define('flavor-picker', FlavorPickerComponent);
}
